import { useEffect, useId, useRef, type ReactNode } from 'react';
import './BottomSheet.css';

export interface BottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Required — the sheet is a dialog and a dialog without a name is unnavigable. */
  title: string;
  children: ReactNode;
  closeLabel?: string;
}

/**
 * A true bottom sheet (design-system catalogue, Tier 2) — the mobile navigation
 * pattern [08 §2](../platform/08-information-architecture.md) mandates, and the
 * one `apps/settings` did not have (TODO B-50: it rendered the desktop `Rail` at
 * every breakpoint, which fit on a phone without being the documented pattern).
 *
 * Deliberately **not** `Panel` with different CSS. A side drawer and a bottom
 * sheet differ in more than the axis they slide on: a sheet is thumb-reachable,
 * sized to its content rather than the viewport height, and dismissed by a
 * downward gesture. Restyling `Panel` would have produced something that looked
 * like a sheet and behaved like a drawer.
 *
 * Also deliberately hand-rolled rather than Radix `Dialog`: the sheet must not
 * trap scroll on the page behind it the way a modal dialog does — a user
 * flicking the sheet closed and continuing to scroll is the normal interaction,
 * not an edge case. Focus management and Escape are implemented explicitly
 * below so nothing is inherited by accident.
 */
export function BottomSheet({
  open,
  onOpenChange,
  title,
  children,
  closeLabel = 'Close',
}: BottomSheetProps) {
  const titleId = useId();
  const sheetRef = useRef<HTMLDivElement>(null);
  // Where focus came from, so it can be returned — a dialog that drops focus to
  // the top of the document on close is disorienting for keyboard users.
  const returnFocusTo = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    returnFocusTo.current = document.activeElement as HTMLElement | null;
    // Focus the sheet itself rather than its first control: the first item in a
    // nav sheet is a destination, and focusing it makes the sheet sound like
    // "Dashboard" instead of announcing what just opened.
    sheetRef.current?.focus();

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onOpenChange(false);
        return;
      }
      if (e.key !== 'Tab' || !sheetRef.current) return;
      // Manual focus trap. Radix would give this for free, but see the docblock
      // for why this is not a Radix dialog.
      const focusable = sheetRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      returnFocusTo.current?.focus?.();
    };
  }, [open, onOpenChange]);

  if (!open) return null;

  return (
    <div className="dpr-sheet">
      {/* Scrim is presentational; the sheet carries the semantics. A click on it
          dismisses, which is the expected gesture on this pattern. */}
      <div
        className="dpr-sheet__scrim"
        aria-hidden="true"
        onClick={() => onOpenChange(false)}
      />
      <div
        className="dpr-sheet__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        ref={sheetRef}
      >
        <div className="dpr-sheet__header">
          {/* A visible grabber, because a sheet with no affordance reads as a
              stuck overlay. aria-hidden: it is a hint, not content. */}
          <span className="dpr-sheet__grabber" aria-hidden="true" />
          <h2 className="dpr-sheet__title" id={titleId}>
            {title}
          </h2>
          <button
            type="button"
            className="dpr-sheet__close"
            onClick={() => onOpenChange(false)}
            aria-label={closeLabel}
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
        <div className="dpr-sheet__body">{children}</div>
      </div>
    </div>
  );
}
